import {AfterViewInit, ChangeDetectionStrategy, Component, OnDestroy, OnInit} from '@angular/core';
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {MemoryFormComponent} from "../shared/memory-form/memory-form.component";
import {LightboxComponent} from "../shared/lightbox/lightbox.component";
import {ActivatedRoute, Router} from "@angular/router";
import {AuthService} from "../_services/auth.service";
import {DuplicateFormComponent} from "../shared/duplicate-form/duplicate-form.component";
import {ContentService} from "../_services/content.service";
import {SnackbarService} from "../_services/snackbar.service";
import {Location} from "@angular/common";
import {PageEvent} from "@angular/material/paginator";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {ConfirmDialogComponent} from "../shared/confirm-dialog/confirm-dialog.component";
import {ConfirmationDialogData} from "../_types/shared";
import {User} from "../_types/user";
import {MediaItem, MemoryComment, MemoryTagsWithMemory, MemoryWithCoverImage} from "../_types/memorybox";
import {GoogleLoaderService} from "../_services/google-loader.service";
import {Subscription, take, timer} from "rxjs";

export type MemoryWithManipulation = Omit<MemoryWithCoverImage, 'latitude' | 'longitude'> & {
    media?: MediaItem[],
    latitude?: number,
    longitude?: number,
}

@Component({
    selector: 'app-memory',
    templateUrl: './memory.component.html',
    styleUrl: './memory.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class MemoryComponent implements OnInit, AfterViewInit, OnDestroy {
    googleLoaded: boolean = false;
    allowsLocation: boolean = false;
    allowsComments: boolean = false;
    locationExpanded: boolean = true;
    commentsExpanded: boolean = true;
    commentsLoading: boolean = true;
    reOrder: boolean = false;
    permission: string | null = null;
    id: number = 0;
    tags: MemoryTagsWithMemory[] = []
    index: number = 0;
    timelineId: number = 0;
    memory: MemoryWithManipulation | undefined;
    user!: User;
    comments: MemoryComment[] = [];
    page: number = 0;
    limit: number = 16;
    count: number = 0;
    loading: boolean = true;
    mediaLoaded: boolean = false;
    ownsTimeline: boolean = false;
    orderLoading: boolean = false;
    isRaa: boolean = false;
    canEdit: boolean = false;
    private pollingSub?: Subscription;

    constructor(public dialog: MatDialog,
                private route: ActivatedRoute,
                private contentService: ContentService,
                private snackbarService: SnackbarService,
                protected authService: AuthService,
                private router: Router,
                private googleLoader: GoogleLoaderService,
                private location: Location,
    ) {
        this.authService.getDynamicSettings().then((r): void => {
            this.allowsComments = r.allow_comments;
            this.allowsLocation = r.location_features;
            this.isRaa = r.is_raa;
        });
        if (window.innerWidth < 1600 && window.innerWidth > 1100) {
            this.limit = 15;
        }
        this.authService.getUser().subscribe({
            next: r => {
                this.user = r;
            }
        })
        this.route.params.subscribe({
            next: params => {
                this.id = params['id'];
                this.index = params['index'];
                this.getMemory().catch((err): void => {
                    console.error(err)
                });
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    ngOnDestroy(): void {
        this.pollingSub?.unsubscribe();
    }

    getMemory(): Promise<any> {
        return new Promise((resolve, reject): void => {
            this.loading = true;
            this.mediaLoaded = false;
            this.contentService.getMemory(this.id).subscribe({
                next: res => {
                    let memory: MemoryWithCoverImage = res;
                    this.timelineId = res.timeline.id;
                    this.ownsTimeline = res.timeline?.owner?.id == this.user?.id;

                    this.canEdit = !!this.user && (memory.creator?.id === this.user.id || !this.user.role.allow_editing_of_own_content_only);

                    if (memory.longitude) {
                        memory.longitude = JSON.parse(memory.longitude);
                    }
                    if (memory.latitude) {
                        memory.latitude = JSON.parse(memory.latitude);
                    }
                    this.memory = memory as MemoryWithManipulation;
                    this.getMedia();
                    this.contentService.viewTimelinePermissions(this.memory.timeline.id).subscribe({
                        next: r => {
                            for (let permission of r) {
                                if (permission.user.id == this.user.id) {
                                    this.permission = permission.permission
                                }
                            }
                            if ((!this.permission || this.permission == 'No permission') && this.user.role.role_type !== 'admin') {
                                this.snackbarService.openSnackBar('Sorry you do not have permission to view this memory', 'error');
                                this.router.navigate(['/noAccess']);
                            } else if (this.user.role.role_type == 'admin') {
                                this.permission = 'View, Comment and Add';
                            }
                            if ((this.permission == 'View and Comment' || this.permission == 'View, Comment and Add') && this.allowsComments) {
                                this.contentService.getMemoryComments(this.id).subscribe({
                                    next: r => {
                                        this.comments = r;
                                        this.commentsLoading = false;
                                        setTimeout((): void => {
                                            // scroll to bottom of chat container
                                            const element: HTMLElement | null = document.getElementById('chatContent');
                                            if (element) {
                                                element.scrollTop = element.scrollHeight;
                                            }
                                        }, 50);
                                    }, error: error => {
                                        this.router.navigate(['/timeline']);
                                        this.snackbarService.openSnackBar(error, 'error')
                                        console.error(error)
                                    }
                                });
                            }
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                            this.router.navigate(['/noAccess']);
                        }
                    });


                    this.contentService.getMemoryTags(this.id).subscribe({
                        next: r => {
                            this.tags = r;
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                        }
                    });
                    resolve('Success')
                }, error: error => {
                    this.snackbarService.openSnackBar(error, 'error')
                    console.error(error)
                    this.loading = false;
                    reject(error)
                }
            });
        })
    }

    getMedia(reload?: boolean): void {
        this.pollingSub?.unsubscribe();

        // Dont show loading wheel on refresh
        this.loading = !reload;
        if (!reload) {
            this.mediaLoaded = false;
        }

        this.contentService.getMemoryMedia(this.id, this.page, this.limit)
            .pipe(take(1))
            .subscribe({
                next: (r): void => {
                    this.count = r.count;
                    if (this.memory) {
                        this.memory.media = r.results || [];
                    }
                    let stillProcessing: boolean = false;
                    const mediaList = this.memory?.media ?? [];
                    for (let i: number = 0; i < mediaList.length; i++) {
                        if (mediaList[i].is_processing) {
                            stillProcessing = true;
                            if (!this.index) {
                                // break loop if not looking for index
                                break
                            }
                        }

                        if (this.index && mediaList[i].id === this.index) {
                            this.viewImage(i);
                            this.index = 0;
                        }
                    }

                    if (stillProcessing) {
                        // Refresh media every 30 seconds while items are processing.
                        this.pollingSub = timer(30000).subscribe((): void => this.getMedia(true));
                    }

                    this.mediaLoaded = true;
                    this.loading = false;
                },
                error: (error): void => {
                    this.snackbarService.openSnackBar(error, 'error');
                    console.error(error);
                    this.loading = false;
                }
            });
    }

    ngOnInit(): void {
        if (window.innerWidth <= 600) {
            this.locationExpanded = false;
            this.commentsExpanded = false;
        }
    }

    async initializeMap(): Promise<void> {
        try {
            await this.googleLoader.loadGoogleMaps();
            this.googleLoaded = true;
        } catch (error) {
            console.error("Maps failed to load", error);
        }
    }

    ngAfterViewInit(): void {
        this.initializeMap();
    }

    recordMedia(): void {
        if (this.dialog.openDialogs.length == 0) {
            import('../shared/create-media/create-media.component')
                .then(({CreateMediaComponent}): void => {
                    const dialogRef: MatDialogRef<any> = this.dialog.open(CreateMediaComponent, {
                        data: this.memory,
                        panelClass: 'fullWidthWindow',
                    });
                    dialogRef.componentInstance.reload.subscribe((): void => {
                        // Refresh memory if when media changes
                        location.reload()
                    });
                })
                .catch((error: unknown): void => {
                    console.error('Upload dialog failed to load', error);
                    this.snackbarService.openSnackBar('A new version of Virtual Memory Box is available. Refreshing now so you can retry.', 'error');
                    setTimeout((): void => window.location.reload(), 1500);
                });
        }
    }

    newMedia(): void {
        if (this.dialog.openDialogs.length == 0) {
            import('../shared/media-form/media-form.component')
                .then(({MediaFormComponent}): void => {
                    const dialogRef: MatDialogRef<any> = this.dialog.open(MediaFormComponent, {
                        data: this.memory,
                        panelClass: 'fullWidthWindow',
                    });
                    dialogRef.componentInstance.reload.subscribe((): void => {
                        // Refresh memory if when media changes
                        location.reload()
                    });
                })
                .catch((error: unknown): void => {
                    console.error('Upload dialog failed to load', error);
                    this.snackbarService.openSnackBar('A new version of Virtual Memory Box is available. Refreshing now so you can retry.', 'error');
                    setTimeout((): void => window.location.reload(), 1500);
                });
        }
    }


    editDetails(): void {
        if (this.dialog.openDialogs.length == 0) {
            window.scrollTo(0, 0);
            // stops google places location pop up from being in the wrong place
            const dialogRef: MatDialogRef<MemoryFormComponent> = this.dialog.open(MemoryFormComponent, {
                data: {memory: this.memory},
                panelClass: 'fullWidthWindow',
            });
            dialogRef.afterClosed().subscribe((resp): void => {
                location.reload();
            });
        }
    }

    viewImage(i: number): void {
        // stop all active media
        document.querySelectorAll('video').forEach(vid => vid.pause());
        document.querySelectorAll('audio').forEach(vid => vid.pause());
        this.dialog.closeAll();
        if (this.memory?.media && this.memory?.media[i]) {
            const dialogRef: MatDialogRef<LightboxComponent> = this.dialog.open(LightboxComponent, {
                data: {
                    media: this.memory.media,
                    index: i,
                    permission: this.permission,
                    timeline: this.memory.timeline.id,
                    canFavourite: this.ownsTimeline
                },
                panelClass: 'fullWidthWindow',
            });
            dialogRef.componentInstance.refresh.subscribe((index): void => {
                // Refresh memory if when media changes
                this.getMemory().then((): void => {
                    dialogRef.close();
                    if (index) {
                        this.viewImage(index)
                    }
                });
            });
            dialogRef.afterClosed().subscribe((): void => {
                // Remove index from param
                this.location.go('memory/' + this.memory?.id);
            });
        }
    }

    duplicateAlbum(): void {
        if (this.dialog.openDialogs.length == 0) {
            const dialogRef: MatDialogRef<DuplicateFormComponent> = this.dialog.open(DuplicateFormComponent, {
                data: {
                    id: this.memory?.id,
                    childId: this.memory?.timeline?.owner?.id
                },
            });
        }
    }

    sendComment(event: any): void {
        this.contentService.createMemoryComment(this.id, event.message).subscribe({
            next: r => {
                this.comments = r;
                this.commentsLoading = false;
                setTimeout((): void => {
                    // scroll to bottom of chat box
                    const element: HTMLElement | null = document.getElementById('chatContent');
                    if (element) {
                        element.scrollTop = element.scrollHeight;
                    }
                }, 50);
            }, error: error => {
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });
    }

    handlePageEvent(e: PageEvent): void {
        this.page = e.pageIndex;
        this.getMedia();
    }

    viewProfile(id: number | undefined): void {
        if (this.user.role.can_access_profiles && id) {
            this.router.navigate(['/profile', id]);
        }
    }

    changeOrder(event: CdkDragDrop<string[]>): void {
        if (!this.memory?.media) return;
        moveItemInArray(this.memory.media, event.previousIndex, event.currentIndex);
    }

    refresh(): void {
        window.location.reload();
    }

    initReOrder(): void {
        this.reOrder = true;
        this.page = 0;
        this.limit = this.count;
        this.getMedia();
    }

    saveReorder(): void {
        if (!this.memory?.media) return;
        this.orderLoading = true;
        let itemsInOrder: number[] = [];
        for (let i: number = 0; i < this.memory.media.length; i++) {
            // loop through all media before this one and set to new order as default order is max.
            itemsInOrder.push(this.memory.media[i].id);
        }
        this.contentService.reOrderMedia(itemsInOrder).subscribe({
            next: r => {
                this.snackbarService.openSnackBar('Order Saved Successfully', 'success')
                setTimeout((): void => {
                    window.location.reload();
                }, 1000);
            }, error: error => {
                this.orderLoading = false;
                this.snackbarService.openSnackBar(error, 'error')
                console.error(error)
            }
        });

    }

    deleteAlbum(): void {
        if (this.user.role.can_permanently_delete) {
            const dialogRef: MatDialogRef<ConfirmDialogComponent> = this.dialog.open<ConfirmDialogComponent, ConfirmationDialogData>(ConfirmDialogComponent, {
                panelClass: 'mediumWindow',
                data: {
                    showSubmitBtn: true,
                    showCancelBtn: true,
                    message: 'Are you sure you want to delete this album?',
                    title: 'Delete Album',
                    showHeader: true
                }
            });
            dialogRef.afterClosed().subscribe((resp): void => {
                if (resp && this.memory?.id) {
                    this.contentService.deleteAlbum(this.memory.id).subscribe({
                        next: r => {
                            this.snackbarService.openSnackBar('Album Deleted', 'success')
                            this.router.navigate(['/timeline', this.timelineId]);
                        }, error: error => {
                            this.snackbarService.openSnackBar(error, 'error')
                            console.error(error)
                        }
                    });
                }
            });
        }
    }


}
